'use strict';
import React, { useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import { Gray } from '@/lenses/uilenses/theme/colors';
import { ExploreMetric } from '@/lenses/uilenses/components/Explore/ExploreMetric';
import { useSelector } from 'react-redux';
import { sampleSetDataCurrentSelector, sampleSetDataDataSelector } from '@/redux/selectors/sampleSetData.selector.js';
import { useBiovizStatus } from '@/lenses/uilenses/hooks/biovizStatus/biovizStatus.hook';
import { useFetchSampleSetsInfoQuery } from "@/redux/services/portal/samplesetsinfo.api";
import { LoadingSpinner } from '@/lenses/uilenses/components/LoadingSpinner/LoadingSpinner';
import { useDiscoveryPanelEntries } from '@/features/discoveryPanels/useDiscoveryPanelEntries';
import { buildProjectTools } from '@/features/projectTools/projectToolsBuilder';
import { projectToolsToExploreItems } from '@/features/projectTools/projectToolsAdapters';
import { API_BIOVIZ } from '@/config/env.config';

export const ProjectHome = ({ sampleSets, match, projectId: projectIdProp }) => {
	const sampleSetData = useSelector(sampleSetDataDataSelector);
	const current = useSelector(sampleSetDataCurrentSelector);
	const projectIdFromStore = sampleSetData[current]?.projectId;
	const projectId = projectIdProp || projectIdFromStore;
	const { entries: discoveryEntries, hasDiscoveryPanels } = useDiscoveryPanelEntries(projectId);
	const { status: biovizStatus, href, isLoading: biovizLoading } = useBiovizStatus(projectId);

	const sampleSetsInfoArg = projectId ? projectId : skipToken;
	const { data: sampleSetsInfo, isLoading: sampleIsLoading } = useFetchSampleSetsInfoQuery(sampleSetsInfoArg);

	const exploreTools = useMemo(
		() =>
			buildProjectTools({
				variant: 'inProject',
				hasDiscoveryPanels,
				discoveryEntries,
				projectCode: match.params.projectCode,
				projectBasePath: match.url,
				hasSpectralData: false,
				biovizStatus,
				biovizLoading,
				biovizHref: href,
				ibpFallbackHref: API_BIOVIZ,
			}),
		[
			hasDiscoveryPanels,
			discoveryEntries,
			match.params.projectCode,
			match.url,
			biovizStatus,
			biovizLoading,
			href,
		],
	);

	const explore = useMemo(
		() => projectToolsToExploreItems(exploreTools, { biovizStatus }),
		[exploreTools, biovizStatus],
	);

	const metrics = useMemo(() => {

		let metabolites = [], stats = 0;
		let sampleSetsCount = 0;
		let metabolitesCount = 0;
		let groupsCount = 0;
		sampleSetsInfo?.forEach((sampleSet) => {
			sampleSetsCount += sampleSet.numSamples;
			metabolitesCount += sampleSet.numMetabolites;
			groupsCount += sampleSet.numComparison;
		});

		sampleSets.forEach((sampleSet) => {
			metabolites.push(...sampleSet.metabolites);
			stats += sampleSet.stats.length;
			[...new Set(metabolites)];
		});

		const studySummary = [{
			label: "Samples",
			value: sampleIsLoading ? <LoadingSpinner size={25} /> : sampleSetsCount,
			infoText: "Number of samples in the sample set.",
		},
		{
			label: "Metabolites",
			value: sampleIsLoading ? <LoadingSpinner size={25} /> : metabolitesCount,
			infoText: "Number of detected metabolites.",
		},
		{
			label: "Groups",
			value: sampleIsLoading ? <LoadingSpinner size={25} /> : groupsCount,
			infoText: "Number of statistical contrasts.",
		}];

		return { studySummary, explore };
	}, [sampleSets, sampleSetsInfo, sampleIsLoading, projectId, explore]);

	return (
		<Box className="ProjectHome">
			<Stack sx={{ background: Gray[100], p: 3 }} spacing={3}>
				<Box
					sx={{ borderRadius: 3, padding: 4, background: "white" }}
					data-testid={"explore"}
				>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							mb: 8,
						}}
					>
						<Typography component="div" variant="h5" sx={{ fontWeight: 600, color: Gray[800] }}>
							Explore
						</Typography>
					</Box>
					<Box sx={{ display: "flex" }}>
						{metrics.explore.map(({ willRender = true, ...metric }, index) => (
							willRender &&
							<ExploreMetric
								key={`explore-metric-${index}`}
								metricsCount={metrics.explore.length}
								{...metric}
							/>
						))}
					</Box>
				</Box>
			</Stack>
		</Box>
	);
};

export default ProjectHome;